import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchOrgRcmSettings,
  resolveOrgDefaultDateWindow,
  toDashboardTimeRange,
  toLastNDaysToken,
} from './orgDefaultDateRange';

export const ORG_DATE_SETTINGS_UPDATED_EVENT = 'rcm-org-date-settings-updated';

export function notifyOrgDateSettingsUpdated() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(ORG_DATE_SETTINGS_UPDATED_EVENT));
  }
}

/**
 * Org/app-level default date range for any RCM service.
 * Reads SaaS organizations.settings.rcm (falls back to last 30 days).
 *
 * Optional fallbackLoader: async () => settings-like object (e.g. service-local settings cache).
 * When preferFallback is true, only the fallback loader is used (Denial Management local settings).
 */
export function useOrgDefaultDateRange(fallbackLoader = null, options = {}) {
  const { preferFallback = false } = options || {};
  const [rcmSettings, setRcmSettings] = useState(null);
  const [ready, setReady] = useState(false);

  const load = useCallback(async () => {
    try {
      if (preferFallback && typeof fallbackLoader === 'function') {
        const fb = await fallbackLoader();
        setRcmSettings(fb && typeof fb === 'object' ? fb : null);
        return;
      }
      let rcm = await fetchOrgRcmSettings();
      if (typeof fallbackLoader === 'function') {
        const fb = await fallbackLoader();
        if (fb && typeof fb === 'object') {
          rcm = {
            ...(rcm || {}),
            default_date_range: fb.default_date_range ?? rcm?.default_date_range,
            default_date_from: fb.default_date_from ?? rcm?.default_date_from,
            default_date_to: fb.default_date_to ?? rcm?.default_date_to,
          };
        }
      }
      setRcmSettings(rcm);
    } catch {
      setRcmSettings(null);
    } finally {
      setReady(true);
    }
  }, [fallbackLoader, preferFallback]);

  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      try {
        const inIframe = typeof window !== 'undefined' && window.self !== window.top;
        const q = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
        const hasToken = Boolean(
          (q && (q.get('auth_token') || q.get('token'))) ||
            localStorage.getItem('token') ||
            localStorage.getItem('auth_token')
        );
        if (inIframe && !hasToken) {
          window.parent.postMessage({ type: 'REQUEST_AUTH_DATA' }, '*');
          await new Promise((r) => setTimeout(r, 450));
        }
      } catch {
        /* ignore */
      }
      if (!cancelled) load();
    };
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [load]);

  useEffect(() => {
    const onUpdate = () => load();
    window.addEventListener(ORG_DATE_SETTINGS_UPDATED_EVENT, onUpdate);

    const onAuthUpdated = () => load();
    window.addEventListener('saas-auth-updated', onAuthUpdated);

    const onMessage = (event) => {
      const data = event?.data;
      if (!data || typeof data !== 'object') return;

      if (data.type === 'AUTH_DATA') {
        let changed = false;
        if (data.authToken) {
          try {
            localStorage.setItem('token', data.authToken);
            localStorage.setItem('auth_token', data.authToken);
            changed = true;
          } catch {
            /* ignore */
          }
        }
        if (data.refreshToken || data.refresh_token) {
          try {
            localStorage.setItem('refresh_token', data.refreshToken || data.refresh_token);
          } catch {
            /* ignore */
          }
        }
        if (data.organizationId) {
          try {
            const id = String(data.organizationId);
            localStorage.setItem('selectedOrganizationId', id);
            localStorage.setItem('organizationId', id);
            localStorage.setItem('organization_id', id);
            changed = true;
          } catch {
            /* ignore */
          }
        }
        if (data.organizationRcmSettings && typeof data.organizationRcmSettings === 'object') {
          try {
            sessionStorage.setItem(
              'organization_rcm_settings',
              JSON.stringify(data.organizationRcmSettings),
            );
            changed = true;
          } catch {
            /* ignore */
          }
        }
        if (changed) load();
        return;
      }

      const rcm =
        data.organizationRcmSettings ||
        (data.type === 'ORGANIZATION_CONTEXT' && data.organizationRcmSettings);
      if (rcm && typeof rcm === 'object') {
        try {
          sessionStorage.setItem('organization_rcm_settings', JSON.stringify(rcm));
        } catch {
          /* ignore */
        }
        load();
      }
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener(ORG_DATE_SETTINGS_UPDATED_EVENT, onUpdate);
      window.removeEventListener('saas-auth-updated', onAuthUpdated);
      window.removeEventListener('message', onMessage);
    };
  }, [load]);

  const defaultWindow = useMemo(
    () => resolveOrgDefaultDateWindow(rcmSettings),
    [rcmSettings]
  );

  const dashboardTimeRange = useMemo(
    () => toDashboardTimeRange(defaultWindow),
    [defaultWindow]
  );

  const lastNDaysToken = useMemo(
    () => toLastNDaysToken(defaultWindow),
    [defaultWindow]
  );

  return {
    rcmSettings,
    defaultWindow,
    dashboardTimeRange,
    lastNDaysToken,
    ready,
    reload: load,
  };
}
